import { IconButton, Typography, styled, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { ADVANCED_TRADE_BUY_SELL_TABS } from "pages/BasicTrade/components/constants";
import BookmarkCoin from "components/BookmarkCoin";
import NumberFormats from "components/NumberFormats";
import StockNumber from "components/StockNumber";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import { getMarketLoggedInList } from "store/slices/market/marketSlice";
import { repeat } from "utils/helpers/interval";
import { setStockNumberColor } from "utils/helpers/numbers";
import useDispatch from "hooks/useDispatch";
import { ROUTE_PATHS } from "routes/paths";

let getMarketLoggedInListInterval = null;

function MarketTable({ marketLoggedInList }) {
  const theme = useTheme();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isFavoriteSorted, setIsFavoriteSorted] = useState(false);

  const [searchParams] = useSearchParams();
  const marketTab =
    +searchParams.get("marketTab") || ADVANCED_TRADE_BUY_SELL_TABS.buyFromUs;
  const allSearchParams = Object.fromEntries([...searchParams]);

  const onSort = (e) => {
    if (e?.key === "bookmark" && e?.sort === "asc") {
      setIsFavoriteSorted("asc");
    } else if (e?.key === "bookmark" && e?.sort === "desc") {
      setIsFavoriteSorted("desc");
    } else {
      setIsFavoriteSorted(null);
    }
  };

  const bookmarkColor = useMemo(() => {
    if (isFavoriteSorted === "asc") {
      return theme.palette.error.main;
    } else if (isFavoriteSorted === "desc") {
      return theme.palette.error.main;
    } else {
      return theme.palette.text.secondary;
    }
  }, [isFavoriteSorted, theme]);

  const headers = [
    {
      key: "bookmark",
      align: "left",
      name: (
        <IconButtonBookmark size="small" iconColor={bookmarkColor}>
          <ion-icon
            name={isFavoriteSorted === "asc" ? "bookmark" : "bookmark-outline"}
          />
        </IconButtonBookmark>
      ),
      width: "40px",
      sortable: true,
      hideSortIcon: true,
      sortKey: "isFavorite",
    },
    {
      key: "symbol",
      align: "left",
      name: "Symbol",
      sortable: true,
      sortKey: "coinSourceName",
    },
    {
      key: "price",
      align: "left",
      name: "Price",
      sortable: true,
      sortKey:
        marketTab === ADVANCED_TRADE_BUY_SELL_TABS.buyFromUs
          ? "priceBuy"
          : "priceSell",
    },
    {
      key: "change",
      align: "right",
      name: "Chg%",
      sortable: true,
      sortKey:
        marketTab === ADVANCED_TRADE_BUY_SELL_TABS.buyFromUs
          ? "changePCT24HourBuy"
          : "changePCT24HourSell",
    },
  ];

  useEffect(() => {
    dispatch(getMarketLoggedInList({ coinDestinationId: -1 }));

    clearInterval(getMarketLoggedInListInterval);

    getMarketLoggedInListInterval = repeat({
      action: () => dispatch(getMarketLoggedInList({ coinDestinationId: -1 })),
    });

    return () => {
      clearInterval(getMarketLoggedInListInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buyFromUsItems = useMemo(() => {
    return marketLoggedInList?.map((data) => ({
      data: data,
      bookmark: <BookmarkCoin data={data} />,
      symbol: (
        <Typography
          variant="subtitle1"
          color="text.main"
          sx={{ cursor: "pointer" }}
        >
          {data?.coinSourceName}/
          <Typography
            variant="subtitle2"
            component="small"
            color="text.secondary"
          >
            {data?.coinDestinationName}
          </Typography>
        </Typography>
      ),
      price: (
        <Typography
          sx={{ cursor: "pointer" }}
          color={setStockNumberColor(data?.changePCT24HourSell)?.color}
          variant="h6"
        >
          <NumberFormats value={data?.priceBuy} />
        </Typography>
      ),
      change: (
        <StockNumber
          sx={{ cursor: "pointer" }}
          justifyContent="flex-end"
          value={data?.changePCT24HourBuy}
        />
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketLoggedInList, dispatch]);

  const sellToUsItems = useMemo(() => {
    return marketLoggedInList?.map((data) => ({
      data: data,
      bookmark: <BookmarkCoin data={data} />,
      symbol: (
        <Typography
          variant="subtitle1"
          color="text.main"
          sx={{ cursor: "pointer" }}
        >
          {data?.coinSourceName}/
          <Typography
            variant="subtitle2"
            component="small"
            color="text.secondary"
          >
            {data?.coinDestinationName}
          </Typography>
        </Typography>
      ),
      price: (
        <Typography
          sx={{ cursor: "pointer" }}
          color={setStockNumberColor(data?.changePCT24HourSell)?.color}
          variant="h6"
        >
          <NumberFormats value={data?.priceSell} />
        </Typography>
      ),
      change: (
        <StockNumber
          sx={{ cursor: "pointer" }}
          justifyContent="flex-end"
          value={data?.changePCT24HourSell}
        />
      ),
    }));
  }, [marketLoggedInList]);

  return (
    <>
      {marketLoggedInList ? (
        <Table
          hideBorders
          headers={headers}
          dense
          onSort={onSort}
          onRowClick={(row) =>
            navigate({
              pathname: ROUTE_PATHS.trade.advanced(row?.data?.coinSourceId, row?.data?.coinDestinationId),
              search: `?${createSearchParams(allSearchParams)}`,
            })
          }
          items={
            marketTab === ADVANCED_TRADE_BUY_SELL_TABS.buyFromUs
              ? buyFromUsItems
              : sellToUsItems
          }
        />
      ) : (
        <TableSkeleton />
      )}
    </>
  );
}

export const IconButtonBookmark = styled(IconButton)(
  ({ theme, iconColor }) => ({
    "ion-icon": {
      position: "relative",
      transform: "rotate(-90deg)",
      fontSize: "16px",
      color: iconColor,
    },
  })
);

export default MarketTable;
